import { useEffect, useRef } from "react";
import styles from "./Designer.module.css";

export const MAX_PRINT_IMAGE_FILE_SIZE = 5 * 1024 * 1024;
export const PRINT_IMAGE_ACCEPT = ".png,.jpg,.jpeg,.svg,.jfif,.webp,image/png,image/jpeg,image/jpg,image/svg+xml,image/jfif,image/webp";

const SUPPORTED_PRINT_IMAGE_MIME_TYPES = new Set([
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/jfif",
    "image/webp",
]);
const SUPPORTED_PRINT_IMAGE_EXTENSION = /\.(png|jpe?g|svg|jfif|webp)$/i;
const PREDESIGN_IMAGE_VERIFY_TIMEOUT_MS = 7000;
const PREDESIGN_SUBTYPES = new Set([5, 8]);
const predesignImageValidityCache = new Map();

export const isPredesignPrint = (print = null) => {
    const subtype = Number(print?.master_print_subtype ?? print?.subtype ?? 0);
    if (PREDESIGN_SUBTYPES.has(subtype)) return true;
    const haystack = String(print?.name || print?.nice_name || print?.factory_name || "").toLowerCase();
    return haystack.includes("predise");
};

export const isSupportedPrintImageFile = (file) => {
    if (!file) return false;
    const mime = String(file.type || "").toLowerCase().trim();
    const byMime = mime ? SUPPORTED_PRINT_IMAGE_MIME_TYPES.has(mime) : false;
    const byExtension = SUPPORTED_PRINT_IMAGE_EXTENSION.test(String(file.name || ""));
    return byMime || byExtension;
};

export const readBlobAsDataUrl = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event?.target?.result || "");
        reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
        reader.readAsDataURL(blob);
    });

const verifyImageUrl = (url) =>
    new Promise((resolve) => {
        const src = String(url || "").trim();
        if (!src) {
            resolve(false);
            return;
        }
        if (predesignImageValidityCache.has(src)) {
            resolve(!!predesignImageValidityCache.get(src));
            return;
        }

        const image = new Image();
        let settled = false;
        const done = (result) => {
            if (settled) return;
            settled = true;
            predesignImageValidityCache.set(src, !!result);
            resolve(!!result);
        };

        const timer = setTimeout(() => done(false), PREDESIGN_IMAGE_VERIFY_TIMEOUT_MS);
        image.onload = () => {
            clearTimeout(timer);
            done(true);
        };
        image.onerror = () => {
            clearTimeout(timer);
            done(false);
        };
        image.src = src;
    });

export const verifyPredesign = async (design) => {
    const candidates = [
        design?.img,
        design?.img_full,
        design?.icon_url,
        design?.img_url,
    ]
        .map((item) => String(item || "").trim())
        .filter(Boolean);

    if (!candidates.length) return false;
    for (const src of candidates) {
        const ok = await verifyImageUrl(src);
        if (ok) return true;
    }
    return false;
};

const drawEmbroideryPredesignPreview = (ctx, image, w, h, fillColor, strokeColor) => {
    const fillCanvas = document.createElement("canvas");
    fillCanvas.width = w;
    fillCanvas.height = h;
    const fillCtx = fillCanvas.getContext("2d");
    if (!fillCtx) return;
    fillCtx.drawImage(image, 0, 0, w, h);
    fillCtx.globalCompositeOperation = "source-in";
    fillCtx.fillStyle = fillColor || "#ffffff";
    fillCtx.fillRect(0, 0, w, h);
    fillCtx.globalCompositeOperation = "source-over";

    const strokeCanvas = document.createElement("canvas");
    strokeCanvas.width = w;
    strokeCanvas.height = h;
    const strokeCtx = strokeCanvas.getContext("2d");
    if (!strokeCtx) return;

    const strokeSource = document.createElement("canvas");
    strokeSource.width = w;
    strokeSource.height = h;
    const strokeSourceCtx = strokeSource.getContext("2d");
    if (!strokeSourceCtx) return;
    strokeSourceCtx.drawImage(image, 0, 0, w, h);
    strokeSourceCtx.globalCompositeOperation = "source-in";
    strokeSourceCtx.fillStyle = strokeColor || fillColor || "#1f2a3d";
    strokeSourceCtx.fillRect(0, 0, w, h);
    strokeSourceCtx.globalCompositeOperation = "source-over";

    const thickness = Math.max(1, Math.round(Math.max(w, h) * 0.025));
    const offsets = [
        [-thickness, 0], [thickness, 0], [0, -thickness], [0, thickness],
        [-thickness, -thickness], [-thickness, thickness], [thickness, -thickness], [thickness, thickness],
    ];
    offsets.forEach(([ox, oy]) => strokeCtx.drawImage(strokeSource, ox, oy));
    strokeCtx.globalCompositeOperation = "destination-out";
    strokeCtx.drawImage(fillCanvas, 0, 0);
    strokeCtx.globalCompositeOperation = "source-over";

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(strokeCanvas, 0, 0);
    ctx.drawImage(fillCanvas, 0, 0);
};

export const PredesignTintPreview = ({ src, colorHex = null, colorHexes = [], isEmbroidery = false }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !src) return;
        const ctx = canvas.getContext("2d");
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            const w = image.naturalWidth || image.width || 1;
            const h = image.naturalHeight || image.height || 1;
            canvas.width = w;
            canvas.height = h;
            ctx.clearRect(0, 0, w, h);
            const hasTint = Boolean(colorHex) || (Array.isArray(colorHexes) && colorHexes.length > 0);
            ctx.drawImage(image, 0, 0, w, h);
            if (!hasTint) {
                return;
            }
            if (isEmbroidery && colorHexes && colorHexes.length > 1) {
                drawEmbroideryPredesignPreview(
                    ctx,
                    image,
                    w,
                    h,
                    colorHexes[0] || "#ffffff",
                    colorHexes[1] || "#1f2a3d"
                );
            } else {
                ctx.globalCompositeOperation = "source-in";
                if (colorHexes && colorHexes.length > 1) {
                    ctx.fillStyle = colorHexes[0] || colorHex || "#ffffff";
                } else {
                    ctx.fillStyle = colorHex || "#ffffff";
                }
                ctx.fillRect(0, 0, w, h);
                ctx.globalCompositeOperation = "source-over";
            }
        };
        image.onerror = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        };
        image.src = src;
    }, [src, colorHex, colorHexes, isEmbroidery]);

    return (
        <canvas
            ref={canvasRef}
            className={styles.littleDesignEditorPreviewImg}
            aria-hidden="true"
        />
    );
};
